import { useState } from "react";
import { OverlayTrigger, Popover } from "react-bootstrap";
import { useIntl } from "react-intl";

import usePermissions from "hooks/usePermissions";
import { useGetShipmentBasic } from "api/queries/shipments/useGetShipmentBasic";
import { useGetQuickRequestDetails } from "api/queries/quick-request/useGetQuickRequestDetails";
import { ReactComponent as DotsVerticalIcon } from "assets/images/iconsV2/dots-vertical.svg";
import testIds from "constants/testIds";

import NotificationMenuItem from "components/molecules/TopNav/common/NavIcons/NotificationsMenuItem";
import GuestAccessMenuItem from "components/molecules/TopNav/common/NavIcons/GuestAccessMenuItem";
import DuplicateMenuItem from "components/molecules/TopNav/shipper/NavIcons/DuplicateMenuItem";
import TemplatesMenuItem from "components/molecules/TopNav/shipper/NavIcons/TemplatesMenuItem";
import CancelMenuItem from "components/molecules/TopNav/shipper/NavIcons/CancelMenuItem";

import DuplicateDialog from "components/molecules/DuplicateShipment/DuplicateDialog";
import SaveTemplateDialog from "components/molecules/SaveShipmentTemplate/SaveTemplateDialog";
import ManageAccessDialog from "components/molecules/TopNav/common/ManageAccess/ManageAccessDialog";
import CancelRequestDialog from "components/molecules/TopNav/CancelRequestDialog";

interface MenuProps {
  isQuickRequest: boolean;
  shipmentId: string;
  tabTitle: string;
}

export default function Menu({ isQuickRequest, shipmentId, tabTitle }: MenuProps) {
  const intl = useIntl();
  const trackingTabTitle = intl.formatMessage({ id: "general.tracking", defaultMessage: "Tracking" });

  const { data: shipmentData }: any = useGetShipmentBasic(shipmentId, {
    enabled: !isQuickRequest && !!shipmentId,
    refetchOnMount: false,
  });
  const { data: quickDetailsData }: any = useGetQuickRequestDetails(shipmentId, {
    enabled: isQuickRequest && !!shipmentId,
    refetchOnMount: false,
  });

  const { shipment } = shipmentData || {};
  const { permissions } = usePermissions();
  const { permissions: quotingPermission, user_type } = JSON.parse(permissions || "{}");
  const isShipper = user_type === "shipper";

  const requesterEmail = isQuickRequest
    ? quickDetailsData?.organization?.quick_request_created_by_email
    : shipmentData?.organization?.requestor_email;
  const shipmentStatus = shipment?.shipment_status;
  const status = isQuickRequest
    ? quickDetailsData?.organization?.quick_request_status
    : shipmentData?.organization?.request_status;
  const allowDuplicate =
    (isQuickRequest && quotingPermission?.quoting?.create_quick_requests) ||
    (!isQuickRequest && quotingPermission?.quoting?.create_quote_requests);
  const isOffPlatform = shipment?.is_off_platform_shipment || false;
  const isShipment = Boolean(shipmentStatus);

  // Permissions
  const canEditCancel = isQuickRequest || (shipment?.request_status !== "Delivered" && !isQuickRequest);
  const canSaveTemplate = isQuickRequest
    ? quotingPermission?.quoting?.create_quick_requests
    : quotingPermission?.quoting?.create_quote_requests;

  const [isGuestAccessDialogOpen, setIsGuestAccessDialogOpen] = useState(false);
  const [isDuplicateDialogOpen, setIsDuplicateDialogOpen] = useState(false);
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
  const [isCancelAndDuplicateDialogOpen, setIsCancelAndDuplicateDialogOpen] = useState(false);
  const [isTemplateDialogOpen, setIsTemplateDialogOpen] = useState(false);

  // More menu items
  const menuItems = [
    isShipment && tabTitle === trackingTabTitle && (
      <GuestAccessMenuItem openGuestAccessDialog={() => setIsGuestAccessDialogOpen(true)} />
    ),
    !isOffPlatform && allowDuplicate && isShipper && (
      <DuplicateMenuItem openDuplicateDialog={() => setIsDuplicateDialogOpen(true)} />
    ),
    canSaveTemplate && isShipper && <TemplatesMenuItem openTemplateDialog={() => setIsTemplateDialogOpen(true)} />,
    !isQuickRequest && <NotificationMenuItem isNotificationEnabled={shipmentData?.settings?.notification_enabled} />,
    !["Canceled", "Shipping", "Archived", "Auto-closed"].includes(status) && canEditCancel && isShipper && (
      <CancelMenuItem
        isQuickRequest={isQuickRequest}
        isShipment={isShipment}
        isShipmentCancelAndDuplicate={false}
        openCancelDialog={() => setIsCancelDialogOpen(true)}
      />
    ),
    isShipment &&
      tabTitle === trackingTabTitle &&
      !["Canceled", "Shipping", "Archived", "Auto-closed"].includes(status) &&
      canEditCancel &&
      isShipper && (
        <CancelMenuItem
          isQuickRequest={isQuickRequest}
          isShipment={isShipment}
          isShipmentCancelAndDuplicate
          openCancelDialog={() => setIsCancelAndDuplicateDialogOpen(true)}
        />
      ),
  ].filter(Boolean);

  return (
    <>
      {/* Dialogs */}
      <CancelRequestDialog
        close={() => setIsCancelDialogOpen(false)}
        isOpen={isCancelDialogOpen}
        isQuickRequest={isQuickRequest}
        isShipment={isShipment}
        isShipmentCancelAndDuplicate={false}
        shipmentId={shipmentId}
        createdByEmail={requesterEmail}
      />
      <CancelRequestDialog
        close={() => setIsCancelAndDuplicateDialogOpen(false)}
        isOpen={isCancelAndDuplicateDialogOpen}
        isQuickRequest={isQuickRequest}
        isShipment={isShipment}
        isShipmentCancelAndDuplicate
        shipmentId={shipmentId}
        createdByEmail={requesterEmail}
      />
      <SaveTemplateDialog
        isOpen={isTemplateDialogOpen}
        close={() => {
          setIsTemplateDialogOpen(false);
        }}
        shipmentId={shipmentId}
      />
      <ManageAccessDialog
        shipmentId={shipmentId}
        isOpen={isGuestAccessDialogOpen}
        close={() => {
          setIsGuestAccessDialogOpen(false);
        }}
      />
      <DuplicateDialog
        isOpen={isDuplicateDialogOpen}
        close={() => {
          setIsDuplicateDialogOpen(false);
        }}
        type={isQuickRequest ? "quick" : "quote"}
        reference={shipmentId}
      />

      {/* More options menu */}
      {menuItems.length > 0 && (
        <OverlayTrigger
          trigger="click"
          placement="bottom-end"
          overlay={
            <Popover id="v2-more-actions-popover" className="v2-more-actions-popover">
              <Popover.Content
                style={{ padding: 0 }}
                className="hide-during-print"
                data-testid={testIds.TOPNAV_V2_MORE_ACTIONS_MENU}
              >
                {menuItems.map((item, index) => (
                  <div
                    key={index}
                    onClick={() => document.body.click()}
                    data-testid={`${testIds.TOPNAV_V2_MORE_ACTIONS_ITEM_PREFIX}${index}`}
                  >
                    {item}
                  </div>
                ))}
              </Popover.Content>
            </Popover>
          }
          rootClose
        >
          <button
            type="button"
            className="topnav-v2__action-btn"
            aria-label="More options"
            data-testid={testIds.TOPNAV_V2_MORE_ACTIONS_BUTTON}
          >
            <DotsVerticalIcon className="topnav-v2__action-icon" data-testid={testIds.TOPNAV_V2_MORE_ACTIONS_ICON} />
          </button>
        </OverlayTrigger>
      )}
    </>
  );
}
